/**
 * O arquivo de uma peça da cadeia de custódia, pelo link do documento emitido (E93.F4).
 *
 * Cada referência do documento emitido imprime `/arquivos/{caso}/{peça}`. A
 * página desse link lê primeiro o REGISTRO da peça — nome, código, tamanho —,
 * que não é exportação e não vai à trilha; o arquivo só desce no clique. As
 * duas leituras passam pelo mesmo recorte do acervo no servidor, então a recusa
 * aparece antes de qualquer byte.
 *
 * Os caminhos são relativos à base `/api` do cliente HTTP.
 */
import axios, { type AxiosInstance } from 'axios';

import type { PecaCustodiada } from '@/lib/cadeia_custodia';
import { codigoDeVerificacao, nomeDoAnexo, type ArquivoBaixado } from '@/lib/http_download';

export type MotivoDaRecusa =
  | 'fora_do_recorte'
  | 'senha_provisoria'
  | 'nao_encontrado'
  | 'nao_confere'
  | 'rede'
  | 'desconhecido';

export class ArquivoDaPecaError extends Error {
  readonly motivo: MotivoDaRecusa;

  constructor(motivo: MotivoDaRecusa) {
    super(motivo);
    this.name = 'ArquivoDaPecaError';
    this.motivo = motivo;
  }
}

const _POR_STATUS: Record<number, MotivoDaRecusa> = {
  403: 'fora_do_recorte',
  404: 'nao_encontrado',
  409: 'nao_confere',
};

/** O gate da senha provisória (E65.F3) recusa com 403 antes de qualquer rota do caso. */
const SENHA_PROVISORIA_PENDENTE = 'SENHA_PROVISORIA_PENDENTE';

/**
 * Status → motivo. O único 403 que não é o recorte do acervo é o da senha
 * provisória, e ele chega na leitura do registro, que é JSON — o link aberto
 * por uma conta recém-criada leva à troca, não a uma recusa de acesso. O
 * download pede `blob`, cujo corpo não se lê sem outra ida assíncrona: ali o
 * status basta.
 */
export function motivoDaRecusa(err: unknown): MotivoDaRecusa {
  if (err instanceof ArquivoDaPecaError) return err.motivo;
  if (!axios.isAxiosError(err)) return 'desconhecido';
  const status = err.response?.status;
  if (status === undefined) return 'rede';
  const detalhe = (err.response?.data as { detail?: unknown } | undefined)?.detail;
  if (status === 403 && detalhe === SENHA_PROVISORIA_PENDENTE) return 'senha_provisoria';
  return _POR_STATUS[status] ?? 'desconhecido';
}

export async function lerPecaDoCaso(
  client: AxiosInstance,
  caseId: string,
  pecaId: string,
): Promise<PecaCustodiada> {
  try {
    const { data } = await client.get<PecaCustodiada>(`/casos/${caseId}/pecas/${pecaId}`);
    return data;
  } catch (err) {
    throw new ArquivoDaPecaError(motivoDaRecusa(err));
  }
}

/** Baixa o arquivo com o nome que o servidor sanear e o código dos bytes entregues. */
export async function baixarArquivoDaPeca(
  client: AxiosInstance,
  caseId: string,
  pecaId: string,
  nomeSugerido: string,
): Promise<ArquivoBaixado> {
  try {
    const resposta = await client.get<Blob>(`/casos/${caseId}/pecas/${pecaId}/arquivo`, {
      responseType: 'blob',
    });
    return {
      blob: resposta.data,
      filename: nomeDoAnexo(resposta.headers['content-disposition'], nomeSugerido),
      codigoVerificacao: codigoDeVerificacao(resposta.headers),
    };
  } catch (err) {
    throw new ArquivoDaPecaError(motivoDaRecusa(err));
  }
}
